import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  ReactNode
} from 'react';
import Keycloak from 'keycloak-js';
import axiosInstance from '../utils/axios';

// Define types for Keycloak instance and token
interface KeycloakTokenParsed {
  exp?: number;
  iat?: number;
  jti?: string;
  iss?: string;
  aud?: string;
  sub?: string;
  typ?: string;
  azp?: string;
  nonce?: string;
  session_state?: string;
  acr?: string;
  allowed_origins?: string[];
  realm_access?: {
    roles: string[];
  };
  resource_access?: {
    [key: string]: {
      roles: string[];
    };
  };
  email?: string;
  email_verified?: boolean;
  preferred_username?: string;
}

interface KeycloakInstance extends Keycloak.KeycloakInstance {
  token?: string;
  tokenParsed?: Keycloak.KeycloakTokenParsed;
  idToken?: string;
  onTokenExpired?: () => void;
}

interface AuthContextType {
  logout: () => void;
  login: () => void;
  authenticated: boolean;
  loading: boolean;
  token: string | null;
  userEmail: string | null;
  username: string | null;
  isAdmin: boolean;
  hasPostFactoAccess: boolean;
}

const AuthContext = createContext<AuthContextType>({
  logout: () => {},
  login: () => {},
  authenticated: false,
  loading: true,
  token: null,
  userEmail: null,
  username: null,
  isAdmin: false,
  hasPostFactoAccess: false
});

interface AuthProviderProps {
  children: ReactNode;
}

// Use environment variables for Keycloak config
const keycloakConfig = {
  url: (import.meta as any).env?.VITE_KEYCLOAK_URL || 'https://auth.neuralkart.com/',
  realm: (import.meta as any).env?.VITE_KEYCLOAK_REALM || 'neural',
  clientId: (import.meta as any).env?.VITE_KEYCLOAK_CLIENT_ID || 'web-rlms',
  credentials: { secret: 'Eyv74aPSg3Lbw6OuTeGfLJlquEaFtwic' },
  onLoad: (import.meta as any).env?.VITE_KEYCLOAK_ONLOAD || "login-required",
};

function debounce<T extends (...args: any[]) => any>(func: T, wait: number): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null;
  return function executedFunction(...args: Parameters<T>): void {
    const later = () => {
      timeout = null;
      func(...args);
    };
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [keycloak, setKeycloak] = useState<KeycloakInstance | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [username, setUsername] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [hasPostFactoAccess, setHasPostFactoAccess] = useState(false);
  const keycloakInstanceRef = useRef<KeycloakInstance | null>(null);
  const initializingRef = useRef(false);
  const tokenRefreshTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isComponentMountedRef = useRef(false);
  const interceptorIdRef = useRef<number | null>(null);

  // Debounced token setter to prevent rapid state updates
  const debouncedSetToken = useCallback(
    debounce((newToken: string) => {
      setToken(newToken);
      try {
        const base64Url = newToken.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        const parsedToken = JSON.parse(jsonPayload) as KeycloakTokenParsed;
        if (parsedToken.email) setUserEmail(parsedToken.email);
        if (parsedToken.preferred_username) setUsername(parsedToken.preferred_username);
        if (parsedToken.realm_access && parsedToken.realm_access.roles) {
          setIsAdmin(parsedToken.realm_access.roles.includes('Admin'));
          setHasPostFactoAccess(parsedToken.realm_access.roles.includes('post_facto_analysis_access'));
        }
      } catch (error) {
        console.error('Error parsing token:', error);
      }
    }, 300),
    []
  );

  // Login function
  const login = useCallback(() => {
    if (keycloakInstanceRef.current) {
      keycloakInstanceRef.current.login();
    }
  }, []);

  // Logout function
  const logout = useCallback(() => {
    if (keycloakInstanceRef.current) {
      localStorage.removeItem('kc_token');
      const kc = keycloakInstanceRef.current;
      const logoutUrl = `${keycloakConfig.url}/realms/${keycloakConfig.realm}/protocol/openid-connect/logout`;
      const postLogoutRedirectUri = encodeURIComponent(window.location.origin);
      const idTokenHint = kc.idToken;
      window.location.href = `${logoutUrl}?post_logout_redirect_uri=${postLogoutRedirectUri}&id_token_hint=${idTokenHint}`;
    }
  }, []);

  // Token refresh mechanism
  const setupTokenRefresh = useCallback(() => {
    if (!keycloakInstanceRef.current) return;
    if (tokenRefreshTimeoutRef.current) clearTimeout(tokenRefreshTimeoutRef.current);
    const expiresIn = (keycloakInstanceRef.current.tokenParsed?.exp || 0) * 1000 - Date.now() - 50000;
    if (expiresIn > 0) {
      tokenRefreshTimeoutRef.current = setTimeout(async () => {
        if (keycloakInstanceRef.current) {
          try {
            const refreshed = await keycloakInstanceRef.current.updateToken(30);
            if (refreshed && keycloakInstanceRef.current && keycloakInstanceRef.current.token) {
              debouncedSetToken(keycloakInstanceRef.current.token);
              localStorage.setItem('kc_token', keycloakInstanceRef.current.token);
              setupTokenRefresh();
            }
          } catch (error) {
            console.error('Failed to refresh token');
            logout();
          }
        }
      }, expiresIn);
    } else {
      if (keycloakInstanceRef.current) {
        (async () => {
          try {
            if (keycloakInstanceRef.current) {
              const refreshed = await keycloakInstanceRef.current.updateToken(30);
              if (refreshed && keycloakInstanceRef.current && keycloakInstanceRef.current.token) {
                debouncedSetToken(keycloakInstanceRef.current.token);
                localStorage.setItem('kc_token', keycloakInstanceRef.current.token);
                setupTokenRefresh();
              }
            }
          } catch (error) {
            console.error('Failed to refresh token');
            logout();
          }
        })();
      }
    }
  }, [debouncedSetToken, logout]);

  // Keycloak initialization
  useEffect(() => {
    if (!isComponentMountedRef.current) {
      isComponentMountedRef.current = true;
      if ((authenticated && keycloakInstanceRef.current) || initializingRef.current) return;
      initializingRef.current = true;
      setLoading(true);
      
      const initializeKeycloak = async () => {
        try {
          if (!keycloakInstanceRef.current) {
            const keycloakInstance = new Keycloak(keycloakConfig) as unknown as KeycloakInstance;
            keycloakInstanceRef.current = keycloakInstance;
          }
          if (keycloakInstanceRef.current) {
            const auth = await keycloakInstanceRef.current.init({
              onLoad: 'login-required',
              checkLoginIframe: false,
              token: localStorage.getItem('kc_token') || undefined
            });
            if (auth && keycloakInstanceRef.current.token) {
              keycloakInstanceRef.current.onTokenExpired = () => {
                console.log('Token expired, logging out automatically');
                logout();
              };
              setKeycloak(keycloakInstanceRef.current);
              setAuthenticated(true);
              debouncedSetToken(keycloakInstanceRef.current.token);
              localStorage.setItem('kc_token', keycloakInstanceRef.current.token);
              setupTokenRefresh();
              
              // Remove existing interceptor if it exists
              if (interceptorIdRef.current !== null) {
                axiosInstance.interceptors.request.eject(interceptorIdRef.current);
              }
              
              // Set up axios auth header
              const interceptorId = axiosInstance.interceptors.request.use(
                (config) => {
                  if (keycloakInstanceRef.current && keycloakInstanceRef.current.token) {
                    config.headers.Authorization = `Bearer ${keycloakInstanceRef.current.token}`;
                    
                    // Add user email to headers if available
                    if (userEmail) {
                      config.headers['X-User-Email'] = userEmail;
                    }
                  }
                  return config;
                },
                (error) => Promise.reject(error)
              );
              
              // Store the interceptor ID for cleanup
              interceptorIdRef.current = interceptorId;
            }
          }
        } catch (error) {
          console.error('Keycloak initialization failed:', error);
        } finally {
          setLoading(false);
          initializingRef.current = false;
        }
      };
      initializeKeycloak();
    }
    return () => {
      if (tokenRefreshTimeoutRef.current) {
        clearTimeout(tokenRefreshTimeoutRef.current);
      }
      // Remove interceptor when component unmounts
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
        interceptorIdRef.current = null;
      }
    };
  }, [authenticated, debouncedSetToken, logout, setupTokenRefresh, userEmail]);

  // Log authentication state only when it changes
  useEffect(() => {
    if (authenticated) {
      console.log('Authenticated:', authenticated);
    }
  }, [authenticated]);

  // Update userEmail in axios headers when it changes
  useEffect(() => {
    if (keycloakInstanceRef.current && userEmail) {
      // Re-create the interceptor with the updated userEmail
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.request.eject(interceptorIdRef.current);
      }
      
      const interceptorId = axiosInstance.interceptors.request.use(
        (config) => {
          if (keycloakInstanceRef.current && keycloakInstanceRef.current.token) {
            config.headers.Authorization = `Bearer ${keycloakInstanceRef.current.token}`;
            
            // Add user email to headers if available
            if (userEmail) {
              config.headers['X-User-Email'] = userEmail;
            }
          }
          return config;
        },
        (error) => Promise.reject(error)
      );
      
      interceptorIdRef.current = interceptorId;
    }
  }, [userEmail]);

  const value = useMemo(() => ({
    logout,
    login,
    authenticated,
    loading,
    token,
    userEmail,
    username,
    isAdmin,
    hasPostFactoAccess
  }), [logout, login, authenticated, loading, token, userEmail, username, isAdmin, hasPostFactoAccess]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 
